<template>
    <v-row v-if="!$store.getters.progress" class="ma-0 pb-offset flex-column flex-nowrap">
        <!-- Tabs -->
        <v-col class="pa-0 flex-grow-0">
            <v-tabs
                show-arrows
                :height="32"
                class="tabs-route"
                background-color="transparent"
                slider-color="primary">
                <v-tab exact-path ripple :to="'/analytics/dashboards/' + $route.params.id">
                    Dashboard
                </v-tab>
            </v-tabs>
        </v-col>

        <!-- Content Row -->
        <v-row class="ma-0 flex-nowrap flex-grow-1">
            <!-- Main Content Area -->
            <v-col :cols="editMode ? 9 : 12" class="pa-1">
            <v-card class="background-2 round-card">
                <!-- Header -->
                <v-card-title class="pa-3">
                    <v-icon left>mdi-view-dashboard</v-icon>
                    <span class="pl-2">{{ dashboard.name }}</span>
                    <v-spacer></v-spacer>

                    <!-- Auto Refresh Toggle -->
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                :color="autoRefresh ? 'success' : 'grey'"
                                @click="toggleAutoRefresh"
                                class="mr-2"
                                :outlined="!autoRefresh"
                                small
                                v-on="on">
                                <v-icon left small>{{ autoRefresh ? 'mdi-autorenew' : 'mdi-autorenew-off' }}</v-icon>
                                {{ autoRefresh ? 'Auto 1m' : 'Auto Refresh' }}
                            </v-btn>
                        </template>
                        <span>{{ autoRefresh ? 'Disattiva auto-refresh (ogni minuto)' : 'Attiva auto-refresh (ogni minuto)' }}</span>
                    </v-tooltip>

                    <!-- Toggle Edit Mode -->
                    <v-btn
                        :color="editMode ? 'warning' : 'grey'"
                        @click="toggleEditMode"
                        class="mr-2"
                        :outlined="!editMode"
                        small>
                        <v-icon left small>{{ editMode ? 'mdi-pencil-off' : 'mdi-pencil' }}</v-icon>
                        {{ editMode ? 'Fine Modifica' : 'Modifica Layout' }}
                    </v-btn>

                    <button-show-generator
                        :id="dashboard.id"
                        :info="info"
                        :model="dashboard"
                    ></button-show-generator>
                </v-card-title>

                <v-divider></v-divider>

                <!-- Descrizione dashboard -->
                <v-card-text v-if="dashboard.description" class="pa-3">
                    <p class="mb-0">{{ dashboard.description }}</p>
                </v-card-text>

                <v-divider v-if="dashboard.description"></v-divider>

                <!-- Grid Layout with Drop Zone -->
                <v-card-text class="pa-3">
                    <div
                        class="grid-container"
                        :class="{ 'drop-zone-active': isDragging && editMode }"
                        @dragover.prevent="onDragOver"
                        @dragleave="onDragLeave"
                        @drop="onDrop">

                        <!-- Drop zone overlay when dragging -->
                        <div v-if="isDragging && editMode && !layout.length" class="drop-zone-placeholder">
                            <v-icon large color="primary">mdi-plus-circle</v-icon>
                            <div class="mt-2">Rilascia qui il report</div>
                        </div>

                        <grid-layout
                            v-if="layout.length"
                            :layout.sync="layout"
                            :col-num="12"
                            :row-height="100"
                            :is-draggable="editMode"
                            :is-resizable="editMode"
                            :vertical-compact="true"
                            :use-css-transforms="true"
                            :margin="[10, 10]"
                            @layout-updated="onLayoutUpdated">

                            <grid-item
                                v-for="item in layout"
                                :key="item.i"
                                :x="item.x"
                                :y="item.y"
                                :w="item.w"
                                :h="item.h"
                                :i="item.i"
                                :min-w="item.minW || 2"
                                :min-h="item.minH || 2"
                                class="grid-item-wrapper">

                                <dashboard-widget-card
                                    ref="widgetCards"
                                    :widget="item"
                                    :edit-mode="editMode"
                                    @remove="removeWidget(item)"
                                ></dashboard-widget-card>
                            </grid-item>
                        </grid-layout>

                        <!-- Empty state quando non ci sono widget -->
                        <v-alert v-if="!layout.length && !loading && !isDragging" type="info" class="mt-3">
                            <v-icon left>mdi-information</v-icon>
                            <span v-if="editMode">Trascina i report dal pannello laterale per aggiungerli alla dashboard.</span>
                            <span v-else>Nessun widget presente. Clicca su "Modifica Layout" per iniziare.</span>
                        </v-alert>
                    </div>

                    <!-- Loading state -->
                    <div v-if="loading" class="text-center py-5">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <p class="mt-3 mb-0">Caricamento dashboard...</p>
                    </div>
                </v-card-text>
            </v-card>
        </v-col>

        <!-- Right Sidebar - Widget Selector (visible in edit mode) -->
        <v-col v-if="editMode" cols="3" class="pa-1 round-card">
            <v-card class="widget-sidebar fill-height background-2 round-card">
                <!-- Header -->
                <v-card-title class="pa-3 sidebar-header">
                    <v-icon left small>mdi-widgets</v-icon>
                    <span class="body-1 font-weight-medium">Report Disponibili</span>
                </v-card-title>

                <v-divider></v-divider>

                <!-- Search field -->
                <div class="pa-2">
                    <v-text-field
                        v-model="search"
                        prepend-icon="mdi-magnify"
                        label="Cerca report..."
                        outlined
                        dense
                        clearable
                        hide-details
                    ></v-text-field>
                </div>

                <v-divider></v-divider>

                <!-- Loading -->
                <div v-if="loadingReports" class="text-center py-4">
                    <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
                </div>


                <!-- Draggable Report list -->
                <div v-else class="report-list pa-2">
                    <div class="caption grey--text mb-2 px-1">
                        <v-icon x-small class="mr-1">mdi-cursor-move</v-icon>
                        Trascina nella dashboard
                    </div>

                    <div
                        v-for="report in filteredReports"
                        :key="report.id"
                        class="draggable-report-item mb-2"
                        draggable="true"
                        @dragstart="onDragStart($event, report)"
                        @dragend="onDragEnd">
                        <v-card outlined class="pa-2 drag-card">
                            <div class="d-flex align-center">
                                <v-icon :color="getChartColor(report.chart_type)" small class="mr-2">
                                    {{ getChartIcon(report.chart_type) }}
                                </v-icon>
                                <div class="flex-grow-1 overflow-hidden">
                                    <div class="body-2 text-truncate">{{ report.name }}</div>
                                    <v-chip x-small outlined>{{ getTypeLabel(report.type) }}</v-chip>
                                </div>
                                <v-icon x-small color="grey" class="drag-handle ml-1">mdi-drag</v-icon>
                            </div>
                        </v-card>
                    </div>

                    <!-- Empty state -->
                    <div v-if="!filteredReports.length && !loadingReports" class="text-center py-4">
                        <v-icon color="grey lighten-1">mdi-file-search-outline</v-icon>
                        <div class="caption grey--text mt-2">Nessun report trovato</div>
                        <div class="caption grey--text">Crea un report in Widget</div>
                    </div>
                </div>
                <v-divider></v-divider>
            </v-card>
        </v-col>
        </v-row>

        <!-- Snackbar per salvataggio -->
        <v-snackbar v-model="saveSnackbar" :timeout="2000" color="success" bottom>
            Layout salvato correttamente
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="saveSnackbar = false">
                    Chiudi
                </v-btn>
            </template>
        </v-snackbar>
    </v-row>
</template>

<script>
import { GridLayout, GridItem } from 'vue-grid-layout'
import DashboardWidgetCard from './DashboardWidgetCard'
import ButtonShowGenerator from '@/components/common/ButtonShowGenerator'

export default {
    name: 'dashboard-builder',

    components: {
        GridLayout,
        GridItem,
        DashboardWidgetCard,
        ButtonShowGenerator
    },

    data() {
        return {
            dashboard: {},
            info: {},
            layout: [],
            editMode: false,
            loading: false,
            layoutChanged: false,
            saveSnackbar: false,
            isDragging: false,
            // Widget selector data
            search: '',
            reports: [],
            loadingReports: false,
            // Auto refresh
            autoRefresh: false,
            refreshIntervalId: null
        }
    },

    computed: {
        filteredReports() {
            if (!this.search) return this.reports
            const s = this.search.toLowerCase()
            return this.reports.filter(r =>
                r.name.toLowerCase().includes(s) ||
                r.type.toLowerCase().includes(s) ||
                (r.description && r.description.toLowerCase().includes(s))
            )
        }
    },

    methods: {
        async loadDashboard() {
            try {
                this.loading = true
                this.$store.commit('startProgress')
                const response = await this.axios.get(`dashboards/${this.$route.params.id}`)

                if (response.data && response.data.model) {
                    this.dashboard = response.data.model
                    this.info = response.data.info
                    this.buildLayout()
                }

                this.$store.commit('endProgress')
                this.loading = false
            } catch (error) {
                this.$store.commit('endProgress')
                this.loading = false
                this.$store.commit('setSnackbarError')
            }
        },

        buildLayout() {
            this.layout = (this.dashboard.widgets || []).map((widget) => {
                const config = widget.layout_config || {}
                return {
                    i: String(widget.id),
                    x: config.x || 0,
                    y: config.y || 0,
                    w: config.w || 4,
                    h: config.h || 2,
                    minW: config.minW || 2,
                    minH: config.minH || 2,
                    widget_id: widget.widget_id,
                    widget: widget.widget,
                    config: config
                }
            })
        },

        toggleEditMode() {
            if (this.editMode && this.layoutChanged) {
                this.saveLayout()
            }
            this.editMode = !this.editMode

            if (this.editMode) {
                this.loadReports()
                this.$store.commit('setSnackbar', {
                    text: 'Modalità modifica attivata. Trascina i widget dal pannello laterale.',
                    color: 'info',
                    value: true
                })
            }
        },

        async loadReports() {
            try {
                this.loadingReports = true
                const response = await this.axios.get('dashboards-available-widgets')
                if (response.data?.status === 'success') {
                    this.reports = response.data.widgets
                }
            } catch (e) {
                this.$store.commit('setSnackbarError')
            } finally {
                this.loadingReports = false
            }
        },

        onLayoutUpdated() {
            if (this.editMode) {
                this.layoutChanged = true
            }
        },

        async saveLayout() {
            try {
                const layoutData = this.layout.map(item => ({
                    widget_id: item.widget_id,
                    x: item.x,
                    y: item.y,
                    w: item.w,
                    h: item.h,
                    minW: item.minW,
                    minH: item.minH,
                    viewMode: item.config?.viewMode || 'chart',
                    autoRefresh: item.config?.autoRefresh || false,
                    refreshInterval: item.config?.refreshInterval || 60
                }))

                const dashboardId = this.dashboard.id || this.$route.params.id
                await this.axios.post(`dashboards/${dashboardId}/layout`, {
                    layout: layoutData
                })

                this.layoutChanged = false
                this.saveSnackbar = true
            } catch (error) {
                this.$store.commit('setSnackbarError')
            }
        },

        onDragStart(event, report) {
            event.dataTransfer.setData('application/json', JSON.stringify(report))
            event.dataTransfer.effectAllowed = 'copy'
            this.isDragging = true
        },

        onDragEnd() {
            this.isDragging = false
        },

        onDragOver(event) {
            if (this.editMode) {
                event.dataTransfer.dropEffect = 'copy'
            }
        },

        onDragLeave() {
            // Gestito da onDragEnd
        },

        async onDrop(event) {
            if (!this.editMode) return

            this.isDragging = false

            try {
                const reportData = event.dataTransfer.getData('application/json')
                if (!reportData) return

                const report = JSON.parse(reportData)
                await this.addWidget(report)
            } catch (e) {
                console.error('Drop error:', e)
            }
        },

        async addWidget(report) {
            const dashboardId = this.dashboard.id || this.$route.params.id
            if (!dashboardId) {
                this.$store.commit('setSnackbar', {
                    text: 'Errore: Dashboard non caricata correttamente',
                    color: 'error',
                    value: true
                })
                return
            }

            try {
                const response = await this.axios.post(`dashboards/${dashboardId}/widgets`, {
                    widget_id: report.id
                })

                if (response.data.status === 'success') {
                    const widget = response.data.widget
                    const config = widget.layout_config || {}

                    this.layout.push({
                        i: String(widget.id),
                        x: config.x || 0,
                        y: config.y || 0,
                        w: config.w || 4,
                        h: config.h || 2,
                        minW: config.minW || 2,
                        minH: config.minH || 2,
                        widget_id: widget.widget_id,
                        widget: widget.widget,
                        config: config
                    })

                    this.$store.commit('setSnackbar', {
                        text: `Widget "${report.name}" aggiunto`,
                        color: 'success',
                        value: true
                    })
                }
            } catch (error) {
                this.$store.commit('setSnackbarError')
            }
        },

        async removeWidget(item) {
            const dashboardId = this.dashboard.id || this.$route.params.id
            try {
                await this.axios.delete(`dashboards/${dashboardId}/widgets/${item.i}`)
                this.layout = this.layout.filter(l => l.i !== item.i)

                this.$store.commit('setSnackbar', {
                    text: 'Widget rimosso',
                    color: 'info',
                    value: true
                })
            } catch (error) {
                this.$store.commit('setSnackbarError')
            }
        },

        getChartIcon(type) {
            const icons = {
                counter: 'mdi-counter',
                pie: 'mdi-chart-pie',
                bar: 'mdi-chart-bar',
                line: 'mdi-chart-line',
                column: 'mdi-chart-bar'
            }
            return icons[type] || 'mdi-table'
        },

        getChartColor(type) {
            const colors = {
                counter: 'purple',
                pie: 'orange',
                bar: 'blue',
                line: 'green',
                column: 'indigo'
            }
            return colors[type] || 'grey'
        },

        getTypeLabel(type) {
            const labels = {
                sales: 'Vendite',
                commissions: 'Provvigioni',
                tickets: 'Ticket',
                subscriptions: 'Abbonamenti',
                financial: 'Finanziario',
                customers: 'Clienti',
                products: 'Prodotti',
                general: 'Generale'
            }
            return labels[type] || type
        },

        toggleAutoRefresh() {
            this.autoRefresh = !this.autoRefresh

            if (this.autoRefresh) {
                this.startAutoRefresh()
                this.$store.commit('setSnackbar', {
                    text: 'Auto-refresh attivato (ogni minuto)',
                    color: 'success',
                    value: true
                })
            } else {
                this.stopAutoRefresh()
                this.$store.commit('setSnackbar', {
                    text: 'Auto-refresh disattivato',
                    color: 'info',
                    value: true
                })
            }
        },

        startAutoRefresh() {
            this.stopAutoRefresh()
            // Refresh immediately
            this.refreshAllWidgets()
            // Then set interval for every 60 seconds
            this.refreshIntervalId = setInterval(() => {
                this.refreshAllWidgets()
            }, 60000)
        },

        stopAutoRefresh() {
            if (this.refreshIntervalId) {
                clearInterval(this.refreshIntervalId)
                this.refreshIntervalId = null
            }
        },

        refreshAllWidgets() {
            // Get all widget card refs and call their refresh method
            if (this.$refs.widgetCards && this.$refs.widgetCards.length) {
                this.$refs.widgetCards.forEach(card => {
                    if (card && card.refreshReport) {
                        card.refreshReport()
                    }
                })
            }
        }
    },

    created() {
        this.loadDashboard()
    },

    beforeDestroy() {
        this.stopAutoRefresh()
        if (this.editMode && this.layoutChanged) {
            this.saveLayout()
        }
    }
}
</script>

<style scoped>
.grid-item-wrapper {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    overflow: hidden;
    background: var(--v-background-base);
}

.theme--dark .grid-item-wrapper {
    border-color: rgba(255, 255, 255, 0.12);
    background: #1E1E1E;
}

.pb-offset {
    padding-bottom: 80px;
}

.grid-item-wrapper:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.theme--dark .grid-item-wrapper:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

/* Drop zone styles */
.grid-container {
    min-height: 300px;
    position: relative;
    transition: all 0.3s ease;
    border-radius: 8px;
}

.drop-zone-active {
    background: rgba(25, 118, 210, 0.05);
    border: 2px dashed #1976D2;
}

.drop-zone-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #1976D2;
    font-weight: 500;
    pointer-events: none;
    z-index: 10;
}

/* Widget Sidebar styles */
.widget-sidebar {
    max-height: calc(100vh - 100px);
    display: flex;
    flex-direction: column;
}

.sidebar-header {
    flex-shrink: 0;
}

.report-list {
    flex: 1;
    overflow-y: auto;
    max-height: calc(100vh - 250px);
}

.draggable-report-item {
    cursor: grab;
}

.draggable-report-item:active {
    cursor: grabbing;
}

.drag-card {
    transition: all 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.theme--dark .drag-card {
    border-color: rgba(255, 255, 255, 0.12) !important;
}

.drag-card:hover {
    border-color: #1976D2 !important;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2);
}

.theme--dark .drag-card:hover {
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.4);
}

.draggable-report-item:active .drag-card {
    transform: scale(0.98);
    opacity: 0.8;
}

.drag-handle {
    opacity: 0.5;
}

.draggable-report-item:hover .drag-handle {
    opacity: 1;
}
</style>

<style>
/* Stili globali per vue-grid-layout */
.vue-grid-item.vue-grid-placeholder {
    background: #1976D2 !important;
    opacity: 0.2;
    border-radius: 8px;
}

.vue-grid-item > .vue-resizable-handle {
    background: transparent;
    border-right: 3px solid #1976D2;
    border-bottom: 3px solid #1976D2;
    border-bottom-right-radius: 8px;
}
</style>
